import type { CSSProperties } from 'react'
import type { ImageAspectRatio, ImageFallbackProps, ImageProps } from './Image.types'
import cn from 'classnames'
import { Icon } from '../icon'
import { Typography } from '../typography'
import { aspectRatioValues } from './aspectRatioValues'
import styles from './BaseImage.module.css'

/**
 * @description BaseImage is an internal component that is not exposed, and
 * contains all UI spec and styles for images.
 */
export function BaseImage({
  ref,
  className,
  aspectRatio = 'flexible',
  isEmpty = false,
  ...props
}: ImageProps) {
  if (isEmpty) {
    return (
      <EmptyImage
        className={className}
        alt={props.alt}
        aspectRatio={aspectRatio}
      />
    )
  }

  return (
    <img
      ref={ref}
      className={cn(
        styles.image,
        { [styles.imageFlexible]: aspectRatio === 'flexible' },
        className,
      )}
      style={{ '--_aspect-ratio': aspectRatioValues[aspectRatio] } as CSSProperties}
      {...props}
    />
  )
}

function EmptyImage({ alt, aspectRatio, className }: ImageFallbackProps & { aspectRatio: ImageAspectRatio }) {
  return (
    <div
      className={cn(styles.emptyStateContent, className)}
      style={{ '--_aspect-ratio': aspectRatioValues[aspectRatio] } as CSSProperties}
    >
      <Icon glyph="ImageSlashed" size={32} />
      {alt && (
        <Typography className={styles.altText} variant="body-s-regular-tight">
          {alt}
        </Typography>
      )}
    </div>
  )
}
